/**
 * The note under Today's "Revenue at risk" figure (operator decision 16). The numerator (GET /today
 * tiles.revenue_at_risk_lakh) is annual revenue of at-risk clients, and its basis is `revenue_source`:
 *   - founder_annual_figure: the founder's typed annual figures, so the total is Σ clients[].annual_revenue_lakh;
 *   - zoho: 12 x the active per-product monthly revenue Zoho keeps, so the total must be on that basis too:
 *     12 x GET /money/overview kpis.total_mrr_lakh (Σ active client_products.mrr), never the founder's figures.
 * Mixing the two bases gave a wrong (even > 100%) share labelled "from Zoho" (review of t-b4d072b9).
 */
import { pct, rupees } from './format';

export interface RevenueNoteInput {
  /** tiles.revenue_at_risk_lakh — annual. */
  rar: number;
  source: 'founder_annual_figure' | 'zoho' | undefined;
  /** Σ clients[].annual_revenue_lakh (the founder's typed annual figures). */
  founderAnnualTotal: number;
  /** GET /money/overview kpis.total_mrr_lakh — monthly; `undefined` while loading or not readable. */
  zohoMonthlyTotal: number | undefined;
}

export function revenueAtRiskNote({ rar, source, founderAnnualTotal, zohoMonthlyTotal }: RevenueNoteInput): string {
  if (source === 'zoho') {
    if (zohoMonthlyTotal === undefined) return 'From Zoho';
    const annual = zohoMonthlyTotal * 12;
    if (!annual) return 'No Zoho revenue yet';
    return `${pct((rar / annual) * 100)} of ${rupees(Math.round(annual * 100) / 100)} a year, from Zoho`;
  }
  const head = founderAnnualTotal
    ? `${pct((rar / founderAnnualTotal) * 100)} of ${rupees(founderAnnualTotal)} a year`
    : 'No annual figures yet';
  return source === 'founder_annual_figure' ? `${head}, from the founder’s annual figures` : head;
}
